import AxiosService from 'services/AxiosService';

import { createSlice, createAction } from '@reduxjs/toolkit'; // use current to get state
import { put, takeLatest } from 'redux-saga/effects';
import pick from 'lodash/pick';
import { ERROR_COUNT_ACTIONS } from 'pages/AutomationEngine/store/consts';
import {
  AUTOMATION_STEPS,
  STEP_NODE_TYPES,
  STEP_NODE_CONFIG,
  getStepConfig,
  defaultTrigger,
  defaultVariableDefinition,
} from 'pages/AutomationEngine/steps';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';

import { createAsyncSaga, takeLeadingAndCancel } from 'store/utilities';
import history from 'routes/history';
import Automation2Service, {
  prepareAutomationForUI,
} from 'services/Automation2Service';
import {
  getAutomation,
  getAutomationMetaData,
  getAutomationExecutionById,
} from '@kizen/api/automation';
import FieldService, { getObjectModelName } from 'services/FieldService';
import { movingStrategies } from '../Engine/dialogs/constants';
import { automationStats, newAutomation } from './example-automations';
import { preProcessDescriptionQues } from 'pages/AutomationEngine/store/utilities';

import { automationCopyLocalStorage } from 'storage/automations';
import {
  defaultValidationState,
  VALIDATION_STATE_TYPES,
  VALIDATION,
  isValidStepId,
} from './validation';
import {
  deleteStep,
  deleteTrigger,
  editStep,
  updateStep,
  updateTrigger,
  clearDescriptionQue,
  setStepError,
  moveOrInsertStep,
  moveLoopStep,
  moveBranchStep,
  mergePathStrategy,
  keepOnlyStrategy,
  moveMergedPaths,
  deleteBranchStrategy,
  moveBranchAndResolveUnreachableSteps,
  applyBranchDeletionStrategy,
  resolveUnreachableStepsAfterGoTo,
  checkTriggerUsedByVariable,
  insertSplitStep,
  insertSplitStepAfterStep,
  insertBranchSplitStep,
  insertMergeStep,
  removeMergeStep,
  setMergeParent,
  removeSplitStep,
  mergePaths,
  figureOutTheNewStep,
  deleteNode,
  insertLoopNextStep,
  wrapStepWithBranch,
  deleteLoop,
  leaveBothStrategyAndInsertBranch,
  addPastedLoopItemVariable,
  daisyChainVariableToActionErrors,
} from './mutations';

import {
  stepsForFlow,
  triggersForFlow,
  automationForApi,
  getAllowNoChildStep,
} from './utilities';

import {
  getChildrenSteps,
  getStepsInMergeMovingBranch,
  splitAndPrepStepsAndVariables,
  insertVariable,
  getUniqueName,
  hasChildren,
  getJoinStep,
  isGoalOrCondition,
  isBranchStep,
  isBranchSplit,
  isMergeJoin,
  doesSplitStepHaveChildrenById,
  isLoopStep,
  getAllStepIdsInMergedBranches,
  safeCurrent,
  getChildrenOfUnmergedBranch,
  isConditionOrGoalSplit,
  getStepIdsInLoop,
  getStepIdsAtMaxLoopDepth,
  getLoopNextStep,
  makeItemVariableName,
  getAllVariableNames,
  getUniqueItemVariableName,
  isArrayVariableInUseByLoop,
  applyItemVariableSource,
  insertItemVariable,
  getClashingLoopArrayVariables,
} from './ts-utilities';

import { StepMovingBlocker } from './types';

import { isVariableInitialization } from 'pages/AutomationEngine/dialogs/VariableWizard/utilities';
import { variablesForFlow } from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/variableDTO';
import { defaultExecutionHistoryErrorState } from './consts';

import { toastVariant } from 'components/ToastProvider';
import { compareAsc, isAfter } from 'date-fns';
import { unsafeRootQueryClient } from 'queries/unsafe-query-client';
import { automationExecutionInfiniteQuery } from 'query/automation';
import { BranchStepTypes } from '../types';
import { MovingBranchOptions } from 'pages/AutomationEngine/constants';

import { automationToNodes } from 'pages/AutomationEngine/Engine/Flow/layout';

const emptyStats = { numberCompleted: 0, numberFailed: 0, numberPending: 0 };

const isNameError = (error) => error?.original?.name;

const isParentStep = (state, { id }) => {
  return state.steps.some((step) => {
    return step.parentKey === id;
  });
};

const clearFocusStep = (state) => {
  state.focusStep = null;
  state.focusStepBySearch = null;
};

const findHistoryIndex = (dates, val) => {
  let low = 0;
  let high = dates.length;
  let mid;
  while (low < high) {
    mid = (low + high) >>> 1;
    if (isAfter(val, dates[mid])) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return dates.length - low;
};

export const loadAutomation = createAsyncSaga(
  'automationEngine/loadAutomation',
  async ({ id, descriptionFallback }) => {
    const [automation, metaData] = await Promise.all([
      getAutomation(AxiosService, id).then(prepareAutomationForUI),
      getAutomationMetaData(AxiosService),
    ]);
    let model = null;

    if (
      automation?.customObject &&
      automation?.customObject?.objectClass === 'custom_objects'
    ) {
      // fetch the whole model as it's useful, to find things like stages etc.
      // NB objectClass is the only thing contained in cutomObject that isn't in the model
      const { customObject } = automation;
      model = await FieldService.getModel({
        id: customObject?.id,
      });

      model = {
        ...model,
        ...customObject,
      };
      automation.customObject = model;
    }

    const { automationData, descriptionQueIds } = preProcessDescriptionQues({
      automation,
      descriptionFallback,
    });
    return {
      metaData,
      automationData,
      descriptionQueIds,
    };
  }
);

const setGoToLoopsValidation = createAction(
  'automationEngine/setGoToLoopsValidation'
);

export const loadFields = createAsyncSaga(
  'automationEngine/loadFields',
  async ({ customObject, type }) => {
    if (type === 'global') {
      return { fields: [], categories: [] };
    }

    try {
      const args = {
        for:
          customObject.name.toLowerCase() !== getObjectModelName('contacts')
            ? customObject
            : customObject.objectClass,
        settingsRequest: true,
      };
      const [fields, categories] = await Promise.all([
        FieldService.getFields(args),
        FieldService.getCategories(args),
      ]);

      return { fields, categories };
    } catch (error) {
      return { fields: [], categories: [] };
    }
  }
);

const persistAutomationInternal = createAsyncSaga(
  'automationEngine/persistAutomation',
  async ({ automation, steps, triggers, variables, model }) => {
    try {
      const payload = automationForApi({
        automation,
        steps,
        triggers,
        variables,
      });

      let newAutomation = null;
      if (!payload.id) {
        newAutomation = await Automation2Service.create(payload);
      } else {
        newAutomation = await Automation2Service.updateFull(payload);
      }

      const { customObject } = newAutomation;
      newAutomation.customObject = {
        ...model,
        ...customObject,
      };

      return { automationData: newAutomation, replace: false };
    } catch (err) {
      console.debug(err);
      throw err;
    }
  }
);

export const persistAutomation = () => {
  return (dispatch, getState) => {
    const state = getState();
    const { automation, steps, triggers, variables, model } = state;

    const goToStepsWithLoop =
      VALIDATION[VALIDATION_STATE_TYPES.GO_TO_STEP_LOOP](state);

    if (goToStepsWithLoop?.length) {
      return dispatch(
        setGoToLoopsValidation({
          type: VALIDATION_STATE_TYPES.GO_TO_STEP_LOOP,
          show: true,
        })
      );
    }

    return dispatch(
      persistAutomationInternal({
        automation,
        steps,
        triggers,
        variables,
        model,
      })
    );
  };
};

const cleanUpDeletedBranchesAndErrorSteps = (state, stepSafe) => {
  const stepIds = state.steps.map(({ id }) => id);

  stepSafe
    .filter(({ id }) => !stepIds.includes(id))
    .forEach(({ id }) => {
      // remove branch of deleted step
      if (state.branches[id]) {
        delete state.branches[id];
      }
      // remove error of deleted step
      if (state.errorSteps.some((err) => err === id)) {
        state.deletedSteps = [...state.deletedSteps, id];

        state.errorSteps = state.errorSteps.filter((err) => err !== id);
      }
    });

  return state;
};

Object.assign(persistAutomation, persistAutomationInternal);

export const refetchAutomation = createAsyncSaga(
  'automationEngine/refetchAutomation',
  async ({ id }) => {
    const automation = await Automation2Service.getById(id);
    return automation;
  }
);

export const loadExecutionHistory = createAsyncSaga(
  'automationEngine/fetchExcutionHistory',
  async ({ executionId, automationId }) => {
    const [executionHistory, modificationHistory, execution] =
      await Promise.all([
        Automation2Service.getAutomationExecutionHistory(executionId, {
          page_size: FORCE_ALL_RECORDS_SIZE,
        }),
        Automation2Service.getAutomationHistory(automationId, {
          page_size: FORCE_ALL_RECORDS_SIZE,
        }),
        getAutomationExecutionById(AxiosService, executionId),
      ]);
    return { executionHistory, modificationHistory, execution };
  }
);

const automationsSlice = createSlice({
  name: 'automationEngine',
  initialState: {
    placing: false,
    lastPlacingStep: false,
    lastPlacingMergeStep: null,
    mergeStepsWithChidren: {},
    visibleAddWhileMoving: {},
    trailingBranchToHide: [],
    mergeAddNodesWhileMoving: {},
    edgesToHideWhileMoving: {},
    lastDroppingStep: false,
    copiedStep: null,
    stepsInMovingBranch: {},
    stepsAtMaxLoopDepth: {},
    movingConditionModal: {
      show: false,
      payload: null,
      isDropOnLastInBranch: false,
      type: null,
    },
    prevSteps: newAutomation.steps,
    prevVariables: newAutomation.variables,
    interrupting: false,
    stepToDelete: { id: null, type: null },
    editStepInfo: null, // { step }
    createStepInfo: null, // { step, id, parentKey, leaf }
    interacting: false,
    dirty: false, // Tracks whether there have been unpersisted changes
    automation: { name: '', active: false },
    metaData: {},
    model: null,
    triggers: newAutomation.triggers,
    steps: newAutomation.steps,
    variables: newAutomation.variables,
    fields: [],
    categories: [],
    branches: {}, // parentKey
    showStats: false,
    stats: automationStats,
    statsRefreshEnabled: true,
    refetching: 0,
    loading: 0,
    persisting: 0,
    fieldsLoaded: false,
    toast: null,
    deleteModal: { id: null, type: null },
    deleteBranchModal: { id: null, type: null },
    deleteLoopModal: { id: null },
    invalidLoopDropModal: [],
    skipModal: { step: null },
    cannotUnmergeModal: false,
    confirmMergeModal: { step: null, forMerge: true },
    validationState: defaultValidationState,
    errorSteps: [],
    deletetedSteps: [],
    dropParent: null,
    allowNoChildStep: null, // flag that is set so the action wizard hides options when required
    newGotoStep: null,
    newGotoStepSafe: [],
    multiInterrupting: null,
    focusStep: null,
    focusStepToken: 0,
    search: '',
    focusStepBySearch: null,
    searchMatchedSteps: [],
    messageDictionary: {},
    automationPlugins: [],
    automationPluginKeys: [],
    nameError: false,
    conditionDescriptionQue: [],
    goalDescriptionQue: [],
    triggerDescriptionQue: [],
    actionDescriptionQue: [],
    delayDescriptionQue: [],
    variableDescriptionQue: [],
    descriptionQueKey: 0,
    revision: null,
    miniMap: { show: false, position: 'bottom-right', size: 'default' },
    historyId: null,
    historyLoaded: false,
    isHistoryFetching: 0,
    historyMeta: null,
    historyErrorDetails: defaultExecutionHistoryErrorState,
    orderedExecutionHistory: [],
    placingVariable: false,
    lastDroppingVariable: false,
    enabledVariableDropzones: {},
    droppingVariable: null,
    executionMode: false,
    debugMode: false,
    currentExecution: null,
    movingBranchType: MovingBranchOptions.GoalCondition,
    stepMovingBlocker: false,
    flashBackgroundColor: null,
  },
  reducers: {
    setGoToLoopsValidation: (state, { payload }) => {
      state.validationState = payload;
      if (payload.show) {
        state.steps = state.steps.map(
          ({ flashBackgroundColor, ...step }) => step
        );
      } else {
        const [firstGoToStepWithLoop] =
          VALIDATION[VALIDATION_STATE_TYPES.GO_TO_STEP_LOOP](state);

        if (firstGoToStepWithLoop) {
          state.steps = state.steps.map((step) => {
            if (step.id === firstGoToStepWithLoop.id) {
              step.flashBackgroundColor =
                STEP_NODE_CONFIG[STEP_NODE_TYPES.ERROR].bgColor;
            }
            return step;
          });
          state.focusStep = firstGoToStepWithLoop.id;
          state.focusStepToken = (state.focusStepToken || 0) + 1;
          state.focusStepBySearch &&= 'skip_search_focus';
        }
      }
    },
    placing: (state, { payload: step }) => {
      // I.e. when dragging a step around the agentic workflow flow
      if (step) {
        const [nodes, edges] = automationToNodes(
          {
            steps: state.steps,
            triggers: state.triggers,
            branches: state.branches,
            variables: state.variables,
          },
          true
        );
        const branchesID = state.steps
          .filter((s) => isBranchStep(s))
          .map(({ id }) => id);

        const joinsOnlyId = state.steps
          .filter((s) => isMergeJoin(s) && branchesID.includes(s.parentKey))
          .map((jo) => jo.id);

        const joins = nodes.filter(
          (n) => n.type === 'joinBranches' && joinsOnlyId.includes(n.id)
        );

        const visibleAddWhileMoving = joins
          .flatMap((j) => {
            return edges.filter((edge) => edge.target === j.id);
          })
          .reduce((prev, edge) => {
            return {
              ...prev,
              [edge.source]: true,
            };
          }, {});

        state.mergeAddNodesWhileMoving = visibleAddWhileMoving;
        state.edgesToHideWhileMoving = {};

        state.placing = true;
        state.lastPlacingStep = step;
        state.movingBranchType = MovingBranchOptions.GoalCondition;
        state.stepMovingBlocker =
          step.type === AUTOMATION_STEPS.go_to_automation_step.type
            ? StepMovingBlocker.Goto
            : false;

        const gotoStepList = state.steps.filter(
          ({ type }) => type === AUTOMATION_STEPS.go_to_automation_step.type
        );

        const allStepsInMergedBranches = [...state.steps]
          .filter(
            (s) =>
              (isGoalOrCondition(s) || isBranchStep(s)) &&
              s.isBranchGroupInitiator
          )
          .flatMap(({ id }) =>
            getStepsInMergeMovingBranch(id, state.steps, false)
          );

        // Only an existing step (one with an id) is being moved. A new one
        // dragged from the control bar has no merge state yet - the create
        // wizard makes it merged when it lands in a loop - so it mustn't be
        // treated as an unmerged goal/condition and blocked from loops.
        if (step.id && isGoalOrCondition(step)) {
          state.placing = true;
          let stepsInMovingBranch = {};

          if (step.isBranchGroupInitiator) {
            const branchJoin = state.steps.find(
              (s) => s.parentKey === step.id && isMergeJoin(s)
            );

            state.trailingBranchToHide =
              branchJoin &&
              !state.steps.some((s) => s.parentKey === branchJoin.id)
                ? [branchJoin.id]
                : [];

            stepsInMovingBranch = getStepsInMergeMovingBranch(
              step.id,
              state.steps
            );
          } else {
            state.stepMovingBlocker = step.goalType
              ? StepMovingBlocker.Goal
              : StepMovingBlocker.Condition;
            stepsInMovingBranch = getChildrenSteps(
              step.id,
              [step.id],
              state.steps
            );

            if (!allStepsInMergedBranches.includes(step.id)) {
              const stepsById = Object.fromEntries(
                state.steps.map((step) => [step.id, step])
              );

              state.edgesToHideWhileMoving = stepsInMovingBranch.reduce(
                (prev, smbId) => {
                  if (isConditionOrGoalSplit(stepsById[smbId])) {
                    const yesChildren = state.steps.some(
                      (s) =>
                        s.parentKey === smbId &&
                        (s.parentCondition || s.fromLabel) === 'yes'
                    );
                    const noChildren = state.steps.some(
                      (s) =>
                        s.parentKey === smbId &&
                        (s.parentCondition || s.fromLabel) === 'no'
                    );

                    if (yesChildren && noChildren) {
                      return prev;
                    }
                    let branches = {};
                    if (!noChildren) {
                      branches = { ...branches, [`${smbId}.add.no`]: smbId };
                    }

                    if (!yesChildren) {
                      branches = { ...branches, [`${smbId}.add.yes`]: smbId };
                    }

                    return { ...prev, ...branches };
                  }

                  const children = state.steps.some(
                    (s) => s.parentKey === smbId
                  );

                  return children ? prev : { ...prev, [`${smbId}.add`]: smbId };
                },
                {}
              );
            }
          }

          if (gotoStepList.length && !state.stepMovingBlocker) {
            state.stepMovingBlocker = gotoStepList.some(({ id }) =>
              stepsInMovingBranch.includes(id)
            )
              ? StepMovingBlocker.Goto
              : false;
          }

          state.stepsInMovingBranch = stepsInMovingBranch.reduce(
            (acc, id) => ({
              ...acc,
              [id]: { showMovingPlaceholder: true },
            }),
            {}
          );
        }

        if (isBranchStep(step)) {
          state.placing = true;
          state.stepMovingBlocker = StepMovingBlocker.Branch;

          if (step.isBranchGroupInitiator) {
            state.movingBranchType = MovingBranchOptions.MergedBranch;

            const branchJoin = state.steps.find(
              (s) => s.parentKey === step.id && isMergeJoin(s)
            );

            state.trailingBranchToHide =
              branchJoin &&
              !state.steps.some((s) => s.parentKey === branchJoin.id)
                ? [branchJoin.id]
                : [];

            const stepsInMovingBranch = getStepsInMergeMovingBranch(
              step.id,
              state.steps
            );

            state.stepsInMovingBranch = stepsInMovingBranch.reduce(
              (acc, id) => ({
                ...acc,
                [id]: { showMovingPlaceholder: true },
              }),
              {}
            );
          } else {
            state.movingBranchType = MovingBranchOptions.UnmergedBranch;

            const childrenOfUnmergedBranch = getChildrenOfUnmergedBranch(
              state.steps,
              step.id,
              state.branches
            );

            const filteredVisibleAddWhileMoving = Object.keys(
              visibleAddWhileMoving
            ).reduce((prev, key) => {
              const id = key.replace('.add', '');
              if (childrenOfUnmergedBranch.includes(id)) {
                return prev;
              }
              return { ...prev, [key]: true };
            }, {});

            // hide the edge to the branch with children
            const branchSplit = state.steps.find(
              (s) => s.parentKey === step.id && isBranchSplit(s)
            );

            state.trailingBranchToHide =
              branchSplit &&
              !state.steps.some((s) => s.parentKey === branchSplit.id)
                ? [branchSplit.id]
                : [];

            // as we are moving an unmerged branch we need to consider steps with no children
            const stepsInMovingBranchNotMerged =
              childrenOfUnmergedBranch.filter(
                (id) =>
                  !allStepsInMergedBranches.includes(id) &&
                  !state.steps.some(({ parentKey }) => parentKey === id)
              );

            state.trailingBranchToHide = [
              ...state.trailingBranchToHide,
              ...stepsInMovingBranchNotMerged,
            ];

            const allStepsOutOffMergedBranches = [...state.steps]
              .filter(
                (s) =>
                  !allStepsInMergedBranches.includes(s.id) &&
                  !childrenOfUnmergedBranch.includes(s.id)
              )
              .map(({ id }) => id);

            const stepsInMovingBranch = childrenOfUnmergedBranch.reduce(
              (acc, id) => {
                const hideEdgeToAdd = !state.steps.some(
                  (s) => s.parentKey === id
                );
                return {
                  ...acc,
                  [id]: { showMovingPlaceholder: true, hideEdgeToAdd },
                };
              },
              {}
            );

            const addNodesOutsideOfBranches = nodes.filter(
              ({ type, data }) =>
                type === 'add' &&
                allStepsOutOffMergedBranches.includes(data.parentKey)
            );

            state.visibleAddWhileMoving = {
              ...filteredVisibleAddWhileMoving,
              ...addNodesOutsideOfBranches,
            };

            const stepsWithChildren = state.steps.reduce(
              (prevSteps, currStep) => {
                if (currStep.id === step.id) {
                  return prevSteps;
                }

                if (stepsInMovingBranch[currStep.id]) {
                  return prevSteps;
                }

                // can't added inside a merged branch
                if (allStepsInMergedBranches.includes(currStep.id)) {
                  return {
                    ...prevSteps,
                    [currStep.id]: {
                      showMovingPlaceholder: true,
                      hideEdgeToAdd: true,
                    },
                  };
                }

                return prevSteps;
              },
              {}
            );

            state.stepsInMovingBranch = {
              ...stepsInMovingBranch,
              ...stepsWithChildren,
            };
          }
        }

        if (isLoopStep(step)) {
          // a loop can't be dropped inside itself - those steps get the default
          // placeholder, and so does the Next/Finish that closes it, since it
          // moves with the loop (AC 6.c)
          const loopNextStepId = getLoopNextStep(state.steps, step)?.id;
          state.stepsInMovingBranch = [
            ...getStepIdsInLoop(state.steps, step),
            ...(loopNextStepId ? [loopNextStepId] : []),
          ].reduce(
            (acc, id) => ({
              ...acc,
              [id]: { showMovingPlaceholder: true },
            }),
            {}
          );

          // nor anywhere already nested as deep as loops are allowed to go -
          // those steps explain the depth limit instead. The loop being dragged
          // and its own body are left out: a loop at the limit would otherwise
          // swap its own card for the blocker mid-drag, unmounting the draggable
          // and leaving the drag stuck.
          const ownLoopIds = new Set([
            step.id,
            ...Object.keys(state.stepsInMovingBranch),
          ]);
          state.stepsAtMaxLoopDepth = getStepIdsAtMaxLoopDepth(state.steps)
            .filter((id) => !ownLoopIds.has(id))
            .reduce((acc, id) => ({ ...acc, [id]: true }), {});
        }

        let lastPlacingMergeStep = null;
        let mergeStepsWithChidren = {};

        if (step.isBranchGroupInitiator) {
          mergeStepsWithChidren = state.steps.reduce((prevSteps, currStep) => {
            const childSteps = state.steps.some(
              (s) => s.parentKey === currStep.id
            );

            return {
              ...prevSteps,
              [currStep.id]: childSteps,
            };
          }, {});

          const mergeStep = state.steps.find(
            (s) => s.parentKey === step.id && s.type === BranchStepTypes.MERGE
          );

          if (mergeStep) {
            const mergeChildren = state.steps.some(
              (s) => s.parentKey === mergeStep.id
            );
            if (!mergeChildren) {
              lastPlacingMergeStep = mergeStep.id;
            }
          }
        }
        state.lastPlacingMergeStep = lastPlacingMergeStep;
        state.mergeStepsWithChidren = mergeStepsWithChidren;
      } else {
        state.placing = false;
        state.stepsInMovingBranch = {};
        state.stepsAtMaxLoopDepth = {};
        state.visibleAddWhileMoving = {};
        state.mergeAddNodesWhileMoving = {};
        state.edgesToHideWhileMoving = {};
        state.lastPlacingMergeStep = null;
        state.mergeStepsWithChidren = {};
        state.visibleAddWhileMoving = {};
        state.trailingBranchToHide = [];

        state.mergeStepsWithChidren = {};
        state.movingBranchType = MovingBranchOptions.GoalCondition;
        state.stepMovingBlocker = false;
      }
    },
    placingVariable: (state, { payload: step }) => {
      // I.e. when dragging a step around the agentic workflow flow
      if (step) {
        state.placingVariable = true;
        state.lastPlacingStep = step;

        const vars = [...state.variables];
        const addNew = vars.pop();
        const draggingIndex = vars.findIndex(({ id }) => id === step.id);
        state.enabledVariableDropzones = vars.reduce(
          (prev, curr, idx) => {
            return {
              ...prev,
              [curr.id]: !(idx === draggingIndex || idx === draggingIndex + 1),
            };
          },
          { [addNew.id]: addNew.order !== draggingIndex + 1 }
        );
      } else {
        if (state.droppingVariable) {
          // position the variable in it's new position
          // N.B. the variables are in the best way for rendering the flow i.e. reversed
          const vars = [...state.variables];
          const addNew = vars.pop();

          const filtered = vars.filter(
            ({ id }) => id !== state.lastPlacingStep.id
          );

          let nextVars;

          if (state.droppingVariable.type === 'initialize_variable_add') {
            nextVars = filtered;
            nextVars.push(state.lastPlacingStep);
          } else {
            nextVars = filtered.reduce((prev, curr) => {
              if (curr.id === state.droppingVariable.id) {
                prev.push(state.lastPlacingStep);
              }
              prev.push(curr);

              return prev;
            }, []);
          }
          nextVars.push(addNew);

          let parentStepId = null;
          const orderVars = nextVars.map((v, order) => {
            const ret = { ...v, order, parentStepId };
            parentStepId = v.id;
            return ret;
          });

          state.variables = orderVars;
          state.dirty = true;
        }

        state.placingVariable = false;
        state.enabledVariableDropzones = {};
        state.droppingVariable = null;
      }
    },
    setDroppingVariable: (state, { payload: step }) => {
      if (step) {
        state.droppingVariable = step;
      } else {
        state.droppingVariable = null;
      }
    },
    addItemVariable: (state, { payload: { itemVariable, currentName } }) => {
      let variables = [...state.variables];

      if (currentName) {
        // Replacing an item reference variable: drop the one it supersedes so
        // steps still pointing at it error on save.
        const current = variables
          .filter(({ stepType, type }) =>
            isVariableInitialization(stepType, type)
          )
          .find((v) => v.details?.variable?.name === currentName);

        if (current) {
          variables = variables.filter((v) => v.id !== current.id);

          // Dropping it from the list is not enough on its own: the steps that
          // referenced the old item variable have to be flagged, exactly as
          // deleting any other variable flags them.
          state = daisyChainVariableToActionErrors(state, { step: current });
        }
      }

      // insertItemVariable re-chains parentStepId across the whole list, which
      // is what keeps the variables in order.
      state.variables = insertItemVariable(variables, itemVariable);
      state.dirty = true;
    },

    // AC: swapping to an array variable of the same item shape - the same
    // Object, or another string/uuid array - keeps the item reference variable,
    // so steps inside the loop go on referencing it. Only the array it draws
    // from changes.
    updateItemVariableSource: (
      state,
      { payload: { name, sourceArrayVariable } }
    ) => {
      const initializations = state.variables.filter((v) =>
        isVariableInitialization(v.stepType, v.type)
      );

      const variable = initializations.find(
        (v) => v.details?.variable?.name === name
      );

      if (!variable) {
        return;
      }

      applyItemVariableSource(
        variable,
        sourceArrayVariable,
        initializations.find(
          (v) => v.details?.variable?.name === sourceArrayVariable
        )
      );

      state.dirty = true;
    },

    interacting: (state, { payload }) => {
      // I.e. when dragging/panning/zooming the agentic workflow flow
      state.interacting = Boolean(payload);
      state.interrupting = false;
      state.stepToDelete = { id: null, type: null };
    },

    interrupting: (state, { payload }) => {
      state.interrupting = Boolean(payload);
    },

    // Hack to prevent Confirmation to leave the page on every time the new notification entity added
    dirty: (state, { payload }) => {
      state.dirty = Boolean(payload);
    },

    rollBack: (state, { payload }) => {
      const { steps } = payload;
      state.steps = steps;
      state.interacting = Boolean(payload);
    },

    newGotoStep: (state, { payload }) => {
      const { newGotoStep } = payload;
      state.newGotoStep = newGotoStep;
    },

    drop: (state, { payload }) => {
      // I.e. when dropping a step onto an add button, or
      // selecting a new step type from the add button menu.
      const {
        step,
        id,
        parentKey,
        leaf,
        label,
        loopDepth,
        loopVariables,
        endOffBranch,
      } = payload;

      state.placing = false;
      state.lastPlacingStep = null;
      state.lastPlacingMergeStep = null;
      state.mergeStepsWithChidren = {};
      state.visibleAddWhileMoving = {};
      state.trailingBranchToHide = [];
      state.mergeAddNodesWhileMoving = {};
      state.edgesToHideWhileMoving = {};

      state.lastDroppingStep = step;
      state.prevSteps = state.steps;
      state.allowNoChildStep = null;
      state.dropParent = { parentKey, label };
      state.movingBranchType = MovingBranchOptions.GoalCondition;
      state.stepMovingBlocker = false;

      if (step?.type === 'go_to_automation_step' && id) {
        state.newGotoStepSafe = [...state.steps];
      } else {
        state.newGotoStepSafe = [];
      }

      const stepConfig =
        step.type === STEP_NODE_TYPES.ACTION ||
        step.type === STEP_NODE_TYPES.AGENT
          ? { creatable: true, prefix: 'action', goalType: false }
          : getStepConfig(step.type);

      if (!step.id && stepConfig.creatable) {
        state.createStepInfo = {
          step: {
            ...step,
            prefix: stepConfig?.prefix || STEP_NODE_TYPES.ACTION,
            goalType: stepConfig?.goalType,
            isBranchGroupInitiator:
              (step.isBranchGroupInitiator ||
                (isBranchStep(step) && (loopDepth ?? 0) !== 0)) ??
              false,
            loopDepth,
            loopVariables,
            endOffBranch,
          },
          id,
          parentKey,
          leaf,
          label,
          loopDepth,
          loopVariables,
          endOffBranch,
        };

        const allowNoChildStep =
          leaf ||
          getAllowNoChildStep(
            parentKey,
            parentKey, // new so pass the parent
            label?.type,
            state.steps
          );
        state.allowNoChildStep = allowNoChildStep;
        return;
      }

      if (isGoalOrCondition(step)) {
        state.dirty = true;
        if (step.isBranchGroupInitiator) {
          state.steps = moveMergedPaths(state, {
            step,
            parentKey,
            leaf,
            label,
            id,
            endOffBranch,
            loopDepth,
            loopVariables,
          });
          state.dirty = true;
        } else {
          // id is the next step after the dropped position
          state.movingConditionModal.isDropOnLastInBranch = id === null;

          state.movingConditionModal.show = true;
          state.movingConditionModal.payload = {
            step,
            id,
            parentKey,
            leaf,
            label,
            endOffBranch,
            loopDepth,
            loopVariables,
          };
          state.movingConditionModal.type = step.type;
        }
      } else if (isBranchStep(step)) {
        state.dirty = true;

        // id is the next step after the dropped position
        if (step.isBranchGroupInitiator) {
          state.steps = moveMergedPaths(state, {
            step,
            parentKey,
            leaf,
            label,
            id,
            endOffBranch,
            loopDepth,
            loopVariables,
          });
          state.dirty = true;
        } else {
          moveBranchStep(state, {
            step,
            id,
            parentKey,
            leaf,
            label,
            endOffBranch,
            loopDepth,
            loopVariables,
          });
        }
      } else if (isLoopStep(step) && step.id) {
        // The loop itself and anything nested in it would all be running
        // inside the loops at the drop position, so none of them may iterate
        // an array one of those is already iterating.
        const clashing = getClashingLoopArrayVariables(
          safeCurrent(state.steps),
          step,
          loopVariables
        );

        if (clashing.length) {
          // Leave the loop where it was and name what clashed.
          state.invalidLoopDropModal = clashing;
          return;
        }

        // a loop moves with its body and its Next/Finish
        state.dirty = true;
        state.steps = moveLoopStep(state, {
          step,
          id,
          parentKey,
          label,
          loopDepth,
          loopVariables,
        });
      } else {
        // add new loopdepth and loopvars
        moveOrInsertStep(state, {
          step,
          id,
          parentKey,
          leaf,
          label,
          endOffBranch,
          loopDepth,
          loopVariables,
        });
      }
    },

    moveCondition: (state, { payload }) => {
      const { strategy, unreachableResolution, deletedGoToDescription } =
        payload;
      const step = state.lastDroppingStep;
      state.dirty = true;
      state.prevSteps = state.steps;

      const stepSafe = [...state.steps];
      switch (strategy) {
        case movingStrategies.ADD_STEPS_TO_YES: {
          moveBranchAndResolveUnreachableSteps(state, {
            ...state.movingConditionModal.payload,
            strategy: 'yes',
            unreachableResolution,
            deletedGoToDescription,
          });
          break;
        }
        case movingStrategies.ADD_STEPS_TO_NO: {
          moveBranchAndResolveUnreachableSteps(state, {
            ...state.movingConditionModal.payload,
            strategy: 'no',
            unreachableResolution,
            deletedGoToDescription,
          });
          break;
        }
        case movingStrategies.MERGE_PATH_STRATEGY: {
          state.steps = mergePathStrategy(state.steps, step.id);
          break;
        }
        case movingStrategies.LEAVE_BOTH_PATHS_SPLIT_STRATEGY: {
          state.steps = leaveBothStrategyAndInsertBranch(
            state.steps,
            step.id,
            state.messageDictionary
          );
          break;
        }
        case movingStrategies.KEEP_ONLY_YES_PATH_STRATEGY: {
          state.steps = keepOnlyStrategy(state.steps, step.id, 'yes');
          break;
        }
        case movingStrategies.KEEP_ONLY_NO_PATH_STRATEGY: {
          state.steps = keepOnlyStrategy(state.steps, step.id, 'no');
          break;
        }
        case movingStrategies.LEAVE_AFTER_MERGE_STRATEGY: {
          state.steps = moveMergedPaths(state, {
            ...state.movingConditionModal.payload,
            strategy: 'yes',
          });
          state.dirty = true;

          break;
        }
        default: {
          // movingStrategies.DELETE_BOTH_PATH_STRATEGY
          state.steps = deleteBranchStrategy(state.steps, step.id);
        }
      }

      if (
        strategy !== movingStrategies.ADD_STEPS_TO_YES &&
        strategy !== movingStrategies.ADD_STEPS_TO_NO &&
        strategy !== movingStrategies.LEAVE_AFTER_MERGE_STRATEGY
      ) {
        moveOrInsertStep(state, state.movingConditionModal.payload);

        // add the merge split back
        state = insertSplitStepAfterStep(state, step);

        if (step.isBranchGroupInitiator) {
          state = insertMergeStep(state, { id: step?.id });
        }

        // joining the paths left behind can leave steps after a "Go To" step
        state = resolveUnreachableStepsAfterGoTo(state, {
          unreachableResolution,
          deletedGoToDescription,
        });
      }
      // if the condition no longer has children and has a branch then delete the branch
      if (!isParentStep(state, step) && state.branches[step.id]) {
        delete state.branches[step.id];
      }
      state = cleanUpDeletedBranchesAndErrorSteps(state, stepSafe);

      state.interrupting = false;
      state.stepsInMovingBranch = {};
      state.stepsAtMaxLoopDepth = {};
      state.visibleAddWhileMoving = {};
      state.mergeAddNodesWhileMoving = {};
      state.edgesToHideWhileMoving = {};

      state.movingConditionModal.show = false;
      state.movingConditionModal.payload = null;
      state.movingConditionModal.isDropOnLastInBranch = false;
      state.movingConditionModal.type = null;
    },
    createStep: (state, { payload }) => {
      // I.e. when submitting step creation from a wizard
      const {
        cancel,
        step,
        id,
        parentKey,
        leaf,
        label,
        endOffBranch,
        loopDepth,
        loopVariables,
      } = payload;

      if (!cancel && !step.id) {
        if (step?.type === 'go_to_automation_step' && id) {
          state.newGotoStep = { ...step, nextStep: id };
          state.newGotoStepSafe = [...state.steps];
        } else {
          state.newGotoStepSafe = [];
        }
        moveOrInsertStep(state, {
          step,
          id,
          parentKey,
          leaf,
          label,
          endOffBranch,
          loopDepth,
          loopVariables,
        });
        if (isGoalOrCondition(step)) {
          state = insertSplitStep(state);
        } else if (isBranchStep(step)) {
          state = insertBranchSplitStep(state);
        } else if (isLoopStep(step)) {
          // update the
          // add loop next
          state = insertLoopNextStep(state);
        }
        if (step.isBranchGroupInitiator) {
          const newStep = figureOutTheNewStep(state);
          safeCurrent(newStep);
          state = mergePaths(state, { step: newStep });

          // }
        }
      }

      state.createStepInfo = null;
      state.allowNoChildStep = null;

      // don't save prev steps if go to as we don't know until after
      if (!(step?.type === 'go_to_automation_step' && id)) {
        state.prevSteps = state.steps;
      }
    },

    editStep: (state, { payload }) => {
      // I.e. when selecting edit in the step's three-dot menu, confirming, or cancelling
      const { cancel, begin, step } = payload;

      if (begin) {
        state.editStepInfo = { step: begin };
        const allowNoChildStep = getAllowNoChildStep(
          begin.parentKey,
          begin.id,
          null,
          state.steps
        );
        state.allowNoChildStep = allowNoChildStep;
        return;
      }

      if (cancel) {
        state.editStepInfo = null;
        return;
      }

      if (step) {
        if (step?.pastedData) {
          const {
            id,
            parentKey,
            leaf,
            label,
            endOffBranch,
            loopDepth,
            loopVariables,
          } = step.pastedData;
          delete step.pastedData;
          if (step?.type === 'go_to_automation_step' && id) {
            state.newGotoStep = { ...step, nextStep: id };
            state.newGotoStepSafe = [...state.steps];

            state.dropParent = { parentKey, label };
          } else {
            state.newGotoStepSafe = [];
          }

          moveOrInsertStep(state, {
            step,
            id,
            parentKey,
            leaf,
            label,
            endOffBranch,
            loopDepth,
            loopVariables,
          });
          if (isGoalOrCondition(step)) {
            // add the merge split
            state = insertSplitStepAfterStep(state, step);

            if (step.isBranchGroupInitiator) {
              // Inside a loop the paths converge on the Next/Finish, so the
              // merge has to know which loop it landed in - without the loop
              // context it is placed after the Next/Finish instead of before.
              state = insertMergeStep(state, {
                id: step?.id,
                loopDepth,
                loopVariables,
              });
            }
          } else if (isBranchStep(step)) {
            // add the branch split
            state = insertBranchSplitStep(state);

            if (step.isBranchGroupInitiator) {
              state = insertMergeStep(state, { id: step?.id });
            }
          } else if (isLoopStep(step)) {
            // a pasted loop brings only its own two cards, and needs an item
            // reference variable of its own rather than the original's
            state = insertLoopNextStep(state);
            state = addPastedLoopItemVariable(state, step);
          }
        } else if (step?.isDuplicate) {
          state.steps = state.steps.map(
            ({ flashBackgroundColor, ...step }) => step
          );
          const { duplicateStepId, newSteps } = wrapStepWithBranch({
            steps: state.steps,
            stepToWrap: step,
            parentKey: step.parentKey,
            insertStepId: step.orgStepId,
            isBranchGroupInitiator: false,
            replace: false,
            messageDictionary: state.messageDictionary,
          });
          state.steps = newSteps;

          state.dirty = true;

          if (isGoalOrCondition(step)) {
            // add the merge split
            state = insertSplitStepAfterStep(state, {
              id: duplicateStepId,
              isBranchGroupInitiator: step.isBranchGroupInitiator,
              goalType: step.goalType,
            });
            if (step.isBranchGroupInitiator) {
              state = insertMergeStep(state, { id: duplicateStepId });
            }
          } else if (isBranchStep(step)) {
            // add the branch split
            state = insertBranchSplitStep(state);

            if (step.isBranchGroupInitiator) {
              state = insertMergeStep(state, { id: step?.id });
            }
          } else if (isLoopStep(step)) {
            // add loop next
            state = insertLoopNextStep(state);
          }

          state.focusStep = duplicateStepId;
          state.focusStepToken = (state.focusStepToken || 0) + 1;
          state.focusStepBySearch &&= 'skip_search_focus';
        } else {
          state = editStep(state, { step });
        }
        state.editStepInfo = null;
      }
    },

    editStepUserDescription: (state, { payload }) => {
      const { step, userDescription } = payload;

      const target = ['steps', 'triggers', 'variables']
        .map((name) => state[name].find((s) => s.id === step?.id))
        .find(Boolean);

      if (target && target.userDescription !== userDescription) {
        target.userDescription = userDescription;
        state.dirty = true;
      }
    },

    editSkipStep: (state, { payload }) => {
      const { step, shouldSkipExecution, continueWithBranch } = payload;

      state = editStep(
        state,
        {
          step: { ...step, shouldSkipExecution, continueWithBranch },
        },
        step.prefix === 'trigger' ? 'triggers' : 'steps'
      );
      state.skipModal = { step: null };
    },

    showSkipModal: (state, { payload: step }) => {
      state.skipModal = { step };
    },

    duplicateStep: (state, { payload: step }) => {
      state.prevSteps = state.steps;
      const newStep = {
        ...step,
        id: null,
        orgStepId: step.id,
        isDuplicate: true,
        stats: emptyStats,
      };

      state.editStepInfo = { step: newStep };
      const allowNoChildStep = getAllowNoChildStep(
        newStep.parentKey,
        newStep.id,
        null,
        state.steps
      );
      state.allowNoChildStep = allowNoChildStep;
    },

    pasteStep: (state, { payload: { step, ...data } }) => {
      state.prevSteps = state.steps;

      // A loop opens a level of its own, so it sits one deeper than the
      // position it was dropped on - the same bump LoopCreateWizard applies
      // when a loop is built from scratch. Everything else lands at the depth
      // of the spot it was pasted into.
      const dropDepth = data?.loopDepth ?? 0;
      const loopDepth = isLoopStep(step) ? dropDepth + 1 : dropDepth;

      const newStep = {
        ...step,
        pastedData: { ...data, loopDepth },
        loopDepth,
        loopVariables: data?.loopVariables ?? [],
      };

      const arrayVariable = newStep?.details?.forEachItem?.arrayVariable;

      if (isLoopStep(newStep) && arrayVariable?.value) {
        if (
          isArrayVariableInUseByLoop(arrayVariable.value, newStep.loopVariables)
        ) {
          // An enclosing loop is already iterating this array, so the copied
          // selection isn't offered at this depth. Clear it rather than carry a
          // value the picker won't show, which leaves the wizard requiring a
          // choice before it can be saved.
          newStep.details = {
            ...newStep.details,
            forEachItem: {
              ...newStep.details.forEachItem,
              arrayVariable: null,
              itemVariable: null,
            },
          };
        } else {
          // Show the name the pasted loop will actually get, rather than the
          // one it was copied from. The variable itself is only created on
          // save, so cancelling the wizard leaves nothing behind.
          newStep.details = {
            ...newStep.details,
            forEachItem: {
              ...newStep.details.forEachItem,
              itemVariable: getUniqueItemVariableName(
                makeItemVariableName(arrayVariable.value),
                getAllVariableNames(state.variables)
              ),
            },
          };
        }
      }

      state.editStepInfo = { step: newStep };
      const allowNoChildStep = getAllowNoChildStep(
        newStep.parentKey,
        newStep.id,
        null,
        state.steps
      );
      state.allowNoChildStep = allowNoChildStep;
    },

    copyStep: (state, { payload: step }) => {
      automationCopyLocalStorage.step = {
        contextObjectId: state?.model?.id,
        automationId: state?.automation?.id,
        step: { ...step, stats: emptyStats, changeType: undefined },
        type: step.type,
      };
      // local storage isn't reactive - this is what tells the add menus to
      // re-read it, so Paste renames itself when a different step is copied
      state.copiedStep = { id: step.id, type: step.type };
    },

    updateStep: (state, { payload }) => {
      const { step, entityName, queName, dirty } = payload;
      state = updateStep(state, { step, entityName, queName, dirty });
    },
    clearDescriptionQue: (state, { payload }) => {
      const { queName } = payload;
      state = clearDescriptionQue(state, { queName });
    },
    updateTrigger: (state, { payload }) => {
      const { trigger, entityName, queName, dirty } = payload;
      state = updateTrigger(state, { trigger, entityName, queName, dirty });
    },
    moveStepsAfterGotoMove: (state, { payload }) => {
      const { step } = payload;

      // The moved "Go To" step usually keeps its original id (it's just
      // reparented), so figureOutTheNewStep finds nothing in that case;
      // fall back to the step that childList[0] (the orphaned step) was
      // reparented under, which is the moved "Go To" step itself.
      const insertStep =
        figureOutTheNewStep(state) ||
        state.steps.find((s) => s.id === step.parentKey);

      const stepIdsInMergeBranches = getAllStepIdsInMergedBranches(state.steps);

      const inMergedBranch =
        !!stepIdsInMergeBranches[state.dropParent.parentKey];

      const { newSteps } = wrapStepWithBranch({
        steps: state.steps,
        stepToWrap: insertStep,
        parentKey: state.dropParent.parentKey,
        insertStepId: insertStep.id,
        isBranchGroupInitiator: inMergedBranch,
        replace: true,
        messageDictionary: state.messageDictionary,
        additionBranchHead: step,
        inMergedBranch: inMergedBranch,
      });

      state.steps = newSteps;
      state.editStepInfo = null;
      state.dropParent = null;
    },
    deleteStep: (state, { payload: id }) => {
      // I.e. when selecting and confirming deletion in the step's three-dot menu
      state.deleteStepType = STEP_NODE_TYPES.ACTION;
      state.stepToDelete = { id, type: STEP_NODE_TYPES.ACTION };

      deleteStep(state, { id });
    },
    forceDeleteStep: (state, { payload: id }) => {
      // I.e. when deleting a step that is used by a goto step
      state.stepToDelete = { id, type: STEP_NODE_TYPES.ACTION };

      deleteStep(state, { id, force: true });
    },
    deleteBranchStep: (state, { payload: id }) => {
      const deleted = state.steps.find((el) => el.id === id);
      if (!deleted) {
        return;
      }

      const hasPendingExecutions =
        state.automation.numberActive > 0 || state.automation.numberPaused > 0;
      if (hasPendingExecutions) {
        state.stepToDelete = { id, type: STEP_NODE_TYPES.BRANCH };
        state.interrupting = true;
        return;
      }

      state.steps = deleteNode(state.steps, deleted);
      state.dirty = true;
    },
    forceDeleteBranchStep: (state, { payload: id }) => {
      const deleted = state.steps.find((el) => el.id === id);
      if (!deleted) {
        return;
      }
      state.steps = deleteNode(state.steps, deleted);
      state.dirty = true;
      state.interrupting = false;
    },
    // Deleting a loop takes its whole body with it, so it is confirmed
    // through a modal rather than run straight off the three-dot menu.
    openDeleteLoopModal: (state, { payload: id }) => {
      state.deleteLoopModal = { id };
    },

    closeDeleteLoopModal: (state) => {
      state.deleteLoopModal = { id: null };
    },

    closeInvalidLoopDropModal: (state) => {
      state.invalidLoopDropModal = [];
    },

    deleteLoop: (state, { payload: id }) => {
      state.deleteLoopModal = { id: null };
      const deleted = state.steps.find((el) => el.id === id);
      if (!deleted) {
        return;
      }
      state = deleteLoop(state, deleted);
    },

    deleteConditionRun: (
      state,
      { payload: { step, strategy, unreachableResolution } }
    ) => {
      // I.e. when selecting and confirming deletion in the step's three-dot menu
      state.stepToDelete = { id: step.id, type: STEP_NODE_TYPES.TRIGGER };
      state.dirty = true;
      state.prevSteps = state.steps;

      const stepSafe = [...state.steps];
      state.steps = applyBranchDeletionStrategy(
        state.steps,
        step.id,
        strategy,
        state.messageDictionary
      );

      state = resolveUnreachableStepsAfterGoTo(state, {
        unreachableResolution,
      });

      state = cleanUpDeletedBranchesAndErrorSteps(state, stepSafe);

      state.interrupting = false;
      state.deleteModal = { id: null, type: null };
    },

    deleteBranchRun: (
      state,
      { payload: { step, strategy, unreachableResolution } }
    ) => {
      // I.e. when selecting and confirming deletion in the step's three-dot menu
      state.stepToDelete = { id: step.id, type: STEP_NODE_TYPES.TRIGGER };
      state.dirty = true;
      state.prevSteps = state.steps;

      const stepSafe = [...state.steps];
      state.steps = applyBranchDeletionStrategy(
        state.steps,
        step.id,
        strategy,
        state.messageDictionary
      );

      state = resolveUnreachableStepsAfterGoTo(state, {
        unreachableResolution,
      });

      state = cleanUpDeletedBranchesAndErrorSteps(state, stepSafe);

      state.interrupting = false;
      state.deleteBranchModal = { id: null, type: null };
    },

    createTrigger: (state) => {
      // I.e. when clicking plus button on trigger's handle
      const newTrigger = {
        ...defaultTrigger,
        id: `${defaultTrigger.id}.${Date.now()}`,
        order: state.triggers.length,
      };
      state.triggers.push(newTrigger);
      state.editStepInfo = { step: newTrigger };
    },
    editTrigger: (state, { payload }) => {
      const { cancel, begin, step } = payload;

      if (begin) {
        state.editStepInfo = { step: begin };
        return;
      }
      if (cancel) {
        state.editStepInfo = null;
        return;
      }
      if (step) {
        state = checkTriggerUsedByVariable(state, { step });

        state = editStep(state, { step }, 'triggers');
        state.editStepInfo = null;
      }
    },
    deleteTrigger: (state, { payload: id }) => {
      // I.e. when selecting and confirming deletion in the triggers's three-dot menu
      state.stepToDelete = { id, type: STEP_NODE_TYPES.TRIGGER };
      deleteTrigger(state, { id });
    },
    forceDeleteTrigger: (state, { payload: id }) => {
      // I.e. when deleting a step that is used by a goto step
      state.stepToDelete = {
        id,
        type: STEP_NODE_TYPES.TRIGGER,
      };
      deleteTrigger(state, {
        id,
        force: true,
      });
    },
    createBranch: (state, { payload: { parentKey, label } }) => {
      // I.e. when selecting a new step type from the add button menu.
      if (parentKey) {
        state.branches[parentKey] = label?.type || true;
      } else if (state.steps.length) {
        // Can only add a variable branch if there are steps
        state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE] = true;
      }
    },
    setAutomation: (state, { payload }) => {
      if (!payload?.revision) {
        state.dirty = true;
      }
      Object.assign(state.automation, payload); // E.g. { name } or { active }
    },
    showStats: (state, { payload }) => {
      state.showStats = Boolean(payload);
    },
    statsRefreshEnabled: (state, { payload }) => {
      state.statsRefreshEnabled = Boolean(payload);
    },
    newAutomation: (state) => {
      // Reset most agentic workflow state
      Object.assign(state, {
        placing: false,
        lastPlacingStep: null,
        lastPlacingMergeStep: null,
        mergeStepsWithChidren: {},
        visibleAddWhileMoving: {},
        trailingBranchToHide: [],
        mergeAddNodesWhileMoving: {},
        edgesToHideWhileMoving: {},
        createStepInfo: null,
        interacting: false,
        dirty: true,
        automation: { name: '', active: false },
        triggers: newAutomation.triggers,
        steps: newAutomation.steps,
        branches: {},
        allowNoChildStep: null,
        revision: null,
        movingBranchType: MovingBranchOptions.GoalCondition,
        stepMovingBlocker: false,
      });
    },
    toast: (state, { payload }) => {
      // add a timestamp to make it unique
      state.toast = { ...payload, ts: Date.now() };
    },
    deleteStepWithModal: (state, { payload }) => {
      const { id, type } = payload;

      const hasPendingExecutions =
        state.automation.numberActive > 0 || state.automation.numberPaused > 0;

      const isSplitStepChildren = doesSplitStepHaveChildrenById(
        state.steps,
        id
      );

      if (!isSplitStepChildren && !hasPendingExecutions) {
        const parent = state.steps.find((s) => s.id === id);

        if (isGoalOrCondition(parent)) {
          const splitStep = state.steps.find(
            (s) => s.parentKey === id && s.type === BranchStepTypes.SPLIT
          );
          state = removeSplitStep(state, { splitStep });
        }

        state.stepToDelete = { id, type: STEP_NODE_TYPES.ACTION };
        deleteStep(state, { id });

        return;
      }
      state.deleteModal = { id, type };
    },

    closeDeleteConditionModal: (state) => {
      state.deleteModal = { id: null, type: null };
    },

    deleteConditionGoalBranchStepWithModal: (state, { payload }) => {
      const { step } = payload;
      state.deleteBranchModal = step;
    },

    closeBranchDeleteConditionModal: (state) => {
      state.deleteBranchModal = { id: null, type: null };
    },

    closeMoveConditionModal: (state) => {
      state.lastDroppingStep = false;
      state.stepsInMovingBranch = {};
      state.stepsAtMaxLoopDepth = {};
      state.visibleAddWhileMoving = {};
      state.mergeAddNodesWhileMoving = {};
      state.edgesToHideWhileMoving = {};
      state.steps = state.prevSteps;
      state.movingConditionModal.show = false;
      state.movingConditionModal.payload = null;
      state.movingConditionModal.isDropOnLastInBranch = false;
      state.movingConditionModal.type = null;
    },
    updateErrors: (state, { payload }) => {
      const { id, action, step = {} } = payload;
      if (
        action === ERROR_COUNT_ACTIONS.add &&
        id &&
        !state.errorSteps.some((err) => err === id) &&
        !state.deletedSteps.some((err) => err === id)
      ) {
        state.errorSteps = [...state.errorSteps, id];
        state = setStepError(state, { step });
      } else if (
        action === ERROR_COUNT_ACTIONS.remove &&
        state.errorSteps.some((err) => err === id) &&
        !state.deletedSteps.some((err) => err === id)
      ) {
        state.deletedSteps = [...state.deletedSteps, id];

        state.errorSteps = state.errorSteps.filter((err) => err !== id);
      }
    },

    prevSteps: (state) => {
      state.prevSteps = state.steps;
    },

    setFocusStep: (state, { payload }) => {
      const { step, highlight } = payload;
      state.focusStep = step;
      state.focusStepBySearch = highlight ? step : null;
      state.focusStepToken = (state.focusStepToken || 0) + 1;
    },

    setSearch: (state, { payload: { search, searchMatchedSteps } }) => {
      const prevFocusStepBySearch = state.focusStepBySearch;
      const searchChanged = state.search !== search;
      state.search = search;

      if (!searchMatchedSteps?.length) {
        state.searchMatchedSteps = [];
        state.focusStepBySearch = null;
      } else {
        state.searchMatchedSteps = searchMatchedSteps;

        if (
          !prevFocusStepBySearch ||
          (searchChanged && !searchMatchedSteps.includes(prevFocusStepBySearch))
        ) {
          state.focusStep = searchMatchedSteps[0];
          state.focusStepBySearch = searchMatchedSteps[0];
          state.focusStepToken = (state.focusStepToken || 0) + 1;
        }
      }
    },
    setMessageDictionary: (state, { payload }) => {
      state.messageDictionary = payload;
    },
    setAutomationPlugins: (state, { payload }) => {
      state.automationPlugins = payload;
      const automationPluginKeys = payload.map(
        ({ plugin_api_name }) => plugin_api_name
      );
      state.automationPluginKeys = automationPluginKeys;
    },

    clearNameError: (state) => {
      // add a timestamp to make it unique
      state.nameError = false;
    },

    fieldsLoaded: (state, { payload }) => {
      state.fieldsLoaded = payload;
    },

    setMiniMap: (state, { payload }) => {
      const miniMap = { ...state.miniMap, ...payload };
      state.miniMap = miniMap;
    },

    setHistoryId: (state, { payload }) => {
      if (state.historyId !== payload) {
        clearFocusStep(state);
      }
      state.historyId = payload;
    },

    showHistoryErrorModal: (state, { payload }) => {
      state.historyErrorDetails = { show: true, error: payload };
    },

    setHistoryErrorDetails: (state, { payload }) => {
      state.historyErrorDetails = payload;
    },

    dropVariableDefinition: (state) => {
      // I.e. when clicking add new
      const newVariableDefinition = {
        ...defaultVariableDefinition,
        type: AUTOMATION_STEPS.initialize_variable_add.type,
        id: `${defaultVariableDefinition.id}.${Date.now()}`,
        order: state.variables.length,
      };

      state.createStepInfo = { step: newVariableDefinition };
    },

    createVariableDefinition: (state, { payload }) => {
      // I.e. when submitting step creation from a wizard
      const { cancel, step, silent } = payload;
      if (!cancel) {
        state.variables = insertVariable([...state.variables], step);

        state.dirty = true;
      }

      if (!silent) {
        state.createStepInfo = null;
      }
    },

    editVariableDefinition: (state, { payload }) => {
      const { cancel, begin, step } = payload;

      if (begin) {
        state.editStepInfo = { step: begin };
        return;
      }
      if (cancel) {
        state.editStepInfo = null;
        return;
      }
      if (step) {
        if (step?.isDuplicate) {
          const duplicateStep = {
            ...step,
            id: `new.${step.type}.${Date.now()}`,
            parentStepId: null,
            isDuplicate: false,
            flashBackgroundColor:
              getStepConfig(
                step.type === 'goal' ? step?.details?.type : step.type
              )?.bgColor ?? null,
            hasError: false,
            stepError: null,
          };

          if (!cancel) {
            state.variables = insertVariable(
              [...state.variables],
              duplicateStep
            );

            state.dirty = true;
          }

          state.focusStep = duplicateStep.id;
          state.focusStepToken = (state.focusStepToken || 0) + 1;
          state.focusStepBySearch &&= 'skip_search_focus';
        } else {
          state = editStep(state, { step }, 'variables');
        }
        state.editStepInfo = null;
      }
    },

    duplicateVariable: (state, { payload: step }) => {
      state.prevVariables = state.variables;
      const { details } = step;
      const { variable } = details;
      const names = state.variables.reduce((prev, curr) => {
        if (curr.details?.variable) {
          prev.push(curr.details.variable.name);
        }
        return prev;
      }, []);
      const name = getUniqueName(details.variable.name, names);

      const newStep = {
        ...{
          ...step,
          details: { ...details, variable: { ...variable, name } },
        },
        id: null,
        orgStepId: step.id,
        isDuplicate: true,
        changeType: undefined,
      };

      state.editStepInfo = { step: newStep };
    },

    deleteVariable: (state, { payload: id }) => {
      // I.e. when selecting and confirming deletion in the step's three-dot menu
      state.deleteStepType = STEP_NODE_TYPES.INITIALIZE_VARIABLE;
      state.stepToDelete = { id, type: STEP_NODE_TYPES.INITIALIZE_VARIABLE };
      deleteStep(state, { id }, 'variables');
    },

    forceDeleteVariable: (state, { payload: id }) => {
      // I.e. when selecting and confirming deletion in the step's three-dot menu
      state.deleteStepType = STEP_NODE_TYPES.INITIALIZE_VARIABLE;
      state.stepToDelete = { id, type: STEP_NODE_TYPES.INITIALIZE_VARIABLE };
      deleteStep(state, { id, force: true }, 'variables');
    },

    setVariables: (state, { payload }) => {
      const { variables, errorIds } = payload;

      for (const id of errorIds) {
        const step = state.variables.find((variable) => variable.id === id);
        if (step && !state.errorSteps.some((err) => err === id)) {
          state.errorSteps = [...state.errorSteps, id];
        }
      }

      state.variables = variables;
    },

    setExecutionMode: (state, { payload }) => {
      if (state.executionMode !== payload) {
        clearFocusStep(state);
      }
      state.executionMode = payload;
    },

    setDebugMode: (state, { payload }) => {
      if (state.debugMode !== payload) {
        clearFocusStep(state);
      }
      state.debugMode = payload;
    },

    setCannotUnmergeModal: (state, { payload }) => {
      state.cannotUnmergeModal = payload;
    },

    setConfirmMergeModal: (state, { payload }) => {
      state.confirmMergeModal = payload;
    },

    clearChangeType: (state) => {
      state.steps = state.steps.map((step) => {
        return { ...step, changeType: undefined };
      });
      state.variables = state.variables.map((variable) => {
        return { ...variable, changeType: undefined };
      });
      state.triggers = state.triggers.map((trigger) => {
        return { ...trigger, changeType: undefined };
      });
    },

    mergePaths: (state, { payload: { step, description } }) => {
      state = mergePaths(state, { step, description });
    },

    unmergePaths: (state, { payload: { step, description } }) => {
      const joinStep = getJoinStep(state.steps, step);
      // just incase it gets called by mistake
      if (joinStep && !hasChildren(state.steps, joinStep)) {
        const ogStep = state.steps.find(({ id }) => id === step?.id);
        const splitStep = state.steps.find(
          ({ parentKey, type }) =>
            parentKey === step?.id &&
            (type === BranchStepTypes.SPLIT ||
              type === BranchStepTypes.BRANCH_SPLIT)
        );
        if (ogStep && splitStep) {
          Object.assign(ogStep, {
            isBranchGroupInitiator: false,
            description: description || ogStep.description,
          });
          Object.assign(splitStep, { isBranchMerged: false });
          state = removeMergeStep(state, { joinStep, splitStep });
          state.dirty = true;
          state = setMergeParent(state, {
            parentStepId: step?.id,
            mergeParent: null,
          });
        }
      }
    },

    resetStepsIfDeleteCanceled: (state) => {
      if (state.newGotoStepSafe.length) {
        state.steps = state.newGotoStepSafe;
      }
      state.newGotoStepSafe = [];

      state.interrupting = false;
      state.multiInterrupting = null;
    },

    resetNewGotoStepSafe: (state) => {
      state.newGotoStepSafe = [];
    },

    setMultiInterrupting: (state, { payload }) => {
      state.multiInterrupting = payload;
      state.lastPlacingStep = null;
    },

    setLastPlacingStep: (state, { payload }) => {
      state.lastPlacingStep = payload;
    },
  },
  extraReducers: {
    [loadAutomation.fulfilled]: (state, { payload }) => {
      const {
        automationData: {
          steps: stepsAndVariables,
          triggers: _triggers,
          ...automation
        },
        metaData,
        descriptionQueIds: {
          conditionIds = [],
          goalIds = [],
          actionIds = [],
          delayIds = [],
          variableIds = [],
        } = {},
      } = payload;

      const [_steps, _variables] =
        splitAndPrepStepsAndVariables(stepsAndVariables);

      const { revision } = automation;

      const { customObject: model } = automation;

      const { triggers, triggerErrors } = triggersForFlow(
        _triggers,
        model,
        state.messageDictionary
      );

      const { steps, stepErrors } = stepsForFlow(
        _steps,
        model,
        state.messageDictionary,
        state.automationPluginKeys,
        state.automationPlugins
      );

      const { variables, variableErrors } = variablesForFlow(
        _variables,
        state.messageDictionary
      );

      const errorSteps = [...triggerErrors, ...stepErrors, ...variableErrors];

      const triggerIds = triggers.map(({ id }) => id);

      Object.assign(state, {
        placing: false,
        lastPlacingStep: null,
        lastPlacingMergeStep: null,
        mergeStepsWithChidren: {},
        visibleAddWhileMoving: {},
        trailingBranchToHide: [],
        mergeAddNodesWhileMoving: {},
        edgesToHideWhileMoving: {},
        createStepInfo: null,
        interacting: false,
        dirty: false,
        automation,
        model,
        triggers: triggers,
        steps: steps,
        variables: variables,
        branches: {},
        errorSteps: errorSteps.map(({ id }) => id),
        deletedSteps: [],
        allowNoChildStep: null,
        conditionDescriptionQue: conditionIds,
        goalDescriptionQue: goalIds,
        triggerDescriptionQue: triggerIds,
        actionDescriptionQue: actionIds,
        delayDescriptionQue: delayIds,
        variableDescriptionQue: variableIds,
        revision: revision,
        miniMap: {
          show: triggerIds.length + steps.length > 5,
          position: 'right',
          size: 'default',
        },
        metaData,
        movingBranchType: MovingBranchOptions.GoalCondition,
        stepMovingBlocker: false,
      });
      if (!state.triggers.length) {
        state.triggers = newAutomation.triggers;
      }
      state.loading -= 1;

      if (state.errorSteps.length) {
        const errorMessages = errorSteps.flatMap(({ errors }) => errors);

        state.toast = {
          variant: toastVariant.FAILURE,
          message: errorMessages?.length
            ? `${errorMessages[0]}${
                errorMessages.length > 1
                  ? `${
                      state.messageDictionary.automationFailedPrefix
                    }${errorMessages.length - 1}${
                      state.messageDictionary.automationFailedSuffix
                    }`
                  : '.'
              }`
            : state.messageDictionary.automationFailedToLoad,
        };
      }
    },

    [loadAutomation.pending]: (state) => {
      state.loading += 1;
    },

    [loadAutomation.rejected]: (state) => {
      state.loading -= 1;
    },

    [loadFields.fulfilled]: (state, { payload }) => {
      const { fields, categories } = payload;
      state.fields = fields;
      state.categories = categories;
      state.loading -= 1;
      state.fieldsLoaded = true;
    },

    [loadFields.pending]: (state) => {
      state.loading += 1;
      state.fieldsLoaded = false;
    },

    [loadFields.rejected]: (state) => {
      state.loading -= 1;
      state.fieldsLoaded = true;
    },
    // also used as fulfillAutomationPersist action
    [persistAutomation.fulfilled]: (state, { payload }) => {
      const {
        automationData: {
          steps: stepsAndVariables,
          triggers: _triggers,
          ...automation
        },
        descriptionQueIds: {
          conditionIds = [],
          goalIds = [],
          actionIds = [],
          delayIds = [],
          variableIds = [],
        } = {},
        replace,
      } = payload;

      const [_steps, _variables] =
        splitAndPrepStepsAndVariables(stepsAndVariables);

      const { revision } = automation;

      const { triggers, triggerErrors } = triggersForFlow(
        _triggers,
        state.model,
        state.messageDictionary
      );

      const { steps, stepErrors } = stepsForFlow(
        _steps,
        state.model,
        state.messageDictionary,
        state.automationPluginKeys,
        state.automationPlugins
      );

      const { variables, variableErrors } = variablesForFlow(
        _variables,
        state.messageDictionary
      );

      Object.assign(state, {
        dirty: !!replace,
        automation,
        triggers: triggers,
        variables: variables,
        steps: steps,
        errorSteps: [...triggerErrors, ...stepErrors, ...variableErrors].map(
          ({ id }) => id
        ),
        deletedSteps: [],
        revision: revision,
      });
      if (replace) {
        const triggerIds = triggers.map(({ id }) => id);

        Object.assign(state, {
          conditionDescriptionQue: conditionIds,
          goalDescriptionQue: goalIds,
          triggerDescriptionQue: triggerIds,
          actionDescriptionQue: actionIds,
          delayDescriptionQue: delayIds,
          variableDescriptionQue: variableIds,
          descriptionQueKey: (state.descriptionQueKey || 0) + 1,
        });
      } else {
        state.persisting -= 1;
      }
      state.branches = pick(
        // Remove stale branch
        state.branches,
        state.steps.map((s) => s.id).concat(STEP_NODE_TYPES.TRIGGER)
      );
    },

    [persistAutomation.pending]: (state) => {
      state.persisting += 1;
    },

    [persistAutomation.rejected]: (state, { error }) => {
      const { original = {} } = error;
      const nameError = Boolean(isNameError(error));

      const errorSteps = [
        ...Object.keys(original || {}),
        ...Object.keys(original?.triggers || {}),
      ]
        .map((key) => key)
        // sometimes 'errors' string exists in this array
        .filter(isValidStepId);

      const steps = state.steps.map((step) => {
        const hasError = errorSteps.includes(step.id);
        let stepError = null;
        if (hasError) {
          if (step.type === 'goal') {
            stepError =
              original[step.id]?.steps?.[0]?.step_goal?.triggers?.[0] ||
              Object.values(original[step.id])[0] ||
              null;
          } else {
            stepError = Object.values(original[step.id])[0] || null;
          }
        }
        return {
          ...step,
          hasError,
          stepError,
        };
      });

      const variables = state.variables.map((step) => {
        const hasError = errorSteps.includes(step.id);
        return {
          ...step,
          hasError,
          stepError: hasError && Object.values(original[step.id])[0],
        };
      });

      const triggers = state.triggers.map((trigger) => {
        const hasError = errorSteps.includes(trigger.id);
        return {
          ...trigger,
          hasError,
          stepError: hasError && original.triggers?.[trigger.id],
        };
      });

      Object.assign(state, {
        steps: steps,
        variables: variables,
        triggers: triggers,
        errorSteps: [...errorSteps],
        focusStep: errorSteps.length ? errorSteps[0] : null,
        focusStepToken: (state.focusStepToken || 0) + 1,
        nameError: nameError,
      });

      state.persisting -= 1;
    },

    [refetchAutomation.fulfilled]: (state, { payload }) => {
      state.refetching -= 1;

      if (payload?.revision !== state.revision) {
        state.toast = {
          variant: toastVariant.FAILURE,
          message: state.messageDictionary.notUpdated,
          ts: Date.now(),
        };
        state.statsRefreshEnabled = false;
      } else {
        const {
          numberActive,
          numberCompleted,
          numberPaused,
          steps: newSteps,
          triggers: newTriggers,
        } = payload;

        const stepStats = newSteps.reduce(
          (acc, step) => ({
            ...acc,
            [step.id]: { ...step.stats },
          }),
          {}
        );

        const triggerStats = newTriggers.reduce(
          (acc, trigger) => ({
            ...acc,
            [trigger.id]: { ...trigger.stats },
          }),
          {}
        );

        const steps = state.steps.map((step) => ({
          ...step,
          stats: stepStats[step.id],
        }));

        const triggers = state.triggers.map((trigger) => ({
          ...trigger,
          stats: triggerStats[trigger.id],
        }));

        state.steps = steps;
        state.triggers = triggers;
        state.automation.numberActive = numberActive;
        state.automation.numberCompleted = numberCompleted;
        state.automation.numberPaused = numberPaused;
      }
    },

    [refetchAutomation.pending]: (state) => {
      state.refetching += 1;
    },

    [refetchAutomation.rejected]: (state) => {
      state.refetching -= 1;
    },

    // loadExecutionHistory
    [loadExecutionHistory.fulfilled]: (
      state,
      { payload: { executionHistory, modificationHistory, execution } }
    ) => {
      state.historyLoaded = true;
      state.isHistoryFetching -= 1;

      unsafeRootQueryClient.invalidateQueries({
        queryKey: automationExecutionInfiniteQuery().queryKey,
      });

      const orderedExecutionHistory = (executionHistory?.results || []).sort(
        (a, b) => compareAsc(a.created, b.created)
      );

      const isDebugMode =
        execution?.debug_mode === 'active' &&
        !(
          execution?.status === 'completed' || execution?.status === 'cancelled'
        );

      const executionDate = orderedExecutionHistory?.[0]?.created || null;

      const modificationHistoryDates = modificationHistory?.results
        ? modificationHistory.results
            .sort((a, b) => compareAsc(a.created, b.created))
            .map((step) => step.created)
        : null;

      const index = findHistoryIndex(modificationHistoryDates, executionDate);
      const {
        triggerDictionary,
        stepDictionary,
        deletedSteps,
        deletedTriggers,
      } = orderedExecutionHistory.reduce(
        (acc, step, index) => {
          if (step.triggerId) {
            acc.triggerDictionary[step.triggerId] = [
              { ...step, index },
              ...(acc.triggerDictionary[step.triggerId] || []),
            ];
          } else if (step.stepId) {
            acc.stepDictionary[step.stepId] = [
              { ...step, index },
              ...(acc.stepDictionary[step.stepId] || []),
            ];
          } else {
            if (index === 0) {
              acc.deletedTriggers += 1;
            } else {
              acc.deletedSteps += 1;
            }
          }

          return acc;
        },
        {
          triggerDictionary: {},
          stepDictionary: {},
          deletedSteps: 0,
          deletedTriggers: 0,
        }
      );

      state.historyMeta = {
        changes: index,
        deletedSteps,
        deletedTriggers,
      };

      state.orderedExecutionHistory = orderedExecutionHistory;

      state.steps = state.steps.map((step) => {
        return {
          ...step,
          history: stepDictionary[step.id] || null,
        };
      });
      state.triggers = state.triggers.map((trigger) => {
        return {
          ...trigger,
          history: triggerDictionary[trigger.id] || null,
        };
      });
      state.variables = state.variables.map((variable) => {
        return {
          ...variable,
          history: stepDictionary[variable.id] || null,
        };
      });
      if (state.debugMode !== isDebugMode) {
        clearFocusStep(state);
      }
      state.debugMode = isDebugMode;
      state.currentExecution = execution;
    },

    [loadExecutionHistory.pending]: (state) => {
      state.isHistoryFetching += 1;
    },
    [loadExecutionHistory.rejected]: (state) => {
      state.historyLoaded = true;
      state.isHistoryFetching -= 1;

      state.historyMeta = {
        changes: 0,
        deletedSteps: {},
        deletedTriggers: {},
      };

      state.orderedExecutionHistory = [];

      state.steps = state.steps.map((step) => {
        return {
          ...step,
          history: null,
        };
      });
      state.triggers = state.triggers.map((trigger) => {
        return {
          ...trigger,
          history: null,
        };
      });
      state.variables = state.variables.map((variable) => {
        return {
          ...variable,
          history: null,
        };
      });
      if (state.debugMode) {
        clearFocusStep(state);
      }
      state.debugMode = false;
      state.currentExecution = null;
    },
  },
});

export const actions = {
  ...automationsSlice.actions,
  loadAutomation,
  loadFields,
  persistAutomation,
  fulfillAutomationPersist: persistAutomation.fulfilled,
  refetchAutomation,
  loadExecutionHistory,
};

export default automationsSlice;

export function* automationEngineSagas(translations) {
  yield takeLatest(loadAutomation, loadAutomation.saga);
  yield takeLatest(loadFields, loadFields.saga);
  yield takeLeadingAndCancel(persistAutomation, persistAutomation.saga);
  yield takeLatest(loadAutomation.rejected, function* saga({ error }) {
    if (error.code === 404) throw new Error(error);
    history.replace('/agentic-workflow/new');
    yield;
  });
  yield takeLatest(
    persistAutomation.rejected,
    function* saga({ error, ...rest }) {
      const { original = {} } = error;

      const nameError = isNameError(error);
      const errorSteps = (Object.keys(original) || [])
        .map((key) => key)
        // sometimes 'errors' string exists in this array
        .filter(isValidStepId);

      // look for first error
      const firstError =
        Array.isArray(original) && errorSteps.length === 0 ? original[0] : null;

      yield put(
        actions.toast({
          variant: toastVariant.FAILURE,
          message: nameError
            ? nameError
            : firstError
              ? firstError
              : translations.error,
        })
      );
    }
  );
  yield takeLatest(persistAutomation.fulfilled, function* saga({ payload }) {
    if (!payload?.replace) {
      yield put(
        actions.toast({
          variant: toastVariant.SUCCESS,
          message: translations.saved,
        })
      );
    }
  });
  yield takeLatest(refetchAutomation, refetchAutomation.saga);
  yield takeLatest(refetchAutomation.rejected, function* saga({ error }) {
    if (error.code === 404) throw new Error(error);

    yield put(
      actions.toast({
        variant: toastVariant.FAILURE,
        message: translations.notRefreshed,
      })
    );
  });
  yield takeLatest(loadExecutionHistory, loadExecutionHistory.saga);
  yield takeLatest(loadExecutionHistory.rejected, function* saga({ error }) {
    if (error.code === 404) {
      throw new Error(error);
    }

    yield put(
      actions.toast({
        variant: toastVariant.FAILURE,
        message: error.original?.detail || translations.historyError,
      })
    );
  });
}
